import {BLEND_MODES} from 'pixi.js';
import {gsap, Power2, Power4} from 'gsap';
import {ReelSpriteSymbol} from '../../../core/reel/symbol/ReelSpriteSymbol';
import {ReelSymbolStates} from '../../../core/reel/symbol/ReelSpriteSymbolArea';
import {MiniPayTable} from '../../../core/extra/MiniPayTable';
import {Helper} from '../../../core/utils/Helper';
import {PunkWinSymbol} from './PunkWinSymbol';
import {PunkRocker} from '../../PunkRocker';
import {PunkSymbolData} from './PunkSymbolData';
import {SymbolData} from '../../../core/reel/symbol/config/SymbolData';
import {ISymbolConfig} from '../../../core/reel/symbol/config/IReelSymbolStruct';
import {ISymbolAnimationConf} from '../../../core/reel/symbol/ReelBaseSymbol';
import {Sequence} from '../../../core/displayobject/Sequence';
import {SymbolMultiplyCounter} from '../../presentations/independent/symbol/SymbolMultiplyCounter';
import {SpinePlayer} from "../../../core/displayobject/SpinePlayer";
import {AnimationEffects} from "../../../core/effects/AnimationEffects";
import {SoundManager} from "../../sound/SoundManager";
import {GameSetting} from "../../../core/setting/GameSetting";
import {ReelEngineManager} from "../../../core/reel/ReelEngineManager";
import {GameConfig} from "../../config/base/GameConfig";
import {OverlayItem} from "../../presentations/independent/animations/OverlayAnimation";
import {ImgLoader} from "@nolimitcity/slot-launcher/bin/loader/ImgLoader";

export class PunkSymbol extends ReelSpriteSymbol {
    private nrOnMult: number = 1;
    private flyByAnimation: Sequence;
    private flashGFX: Sequence;
    private _bgGFX: PIXI.Graphics;
    private fire_FX: Sequence;
    private winSym: PunkWinSymbol[] = [];
    private splitSym: PIXI.Sprite[] = [];
    private divider: Sequence[] = [];
    private multText: SymbolMultiplyCounter;
    private _expandLighteningSpine: SpinePlayer;
    private _expandSpine: SpinePlayer;
    private _expandedSymSize: number;
    private _pipeBombSpine: SpinePlayer;
    private _scatterSpine: SpinePlayer;
    private _overlay:PIXI.Sprite;

    constructor(ID: number, symName: string, state: string = ReelSymbolStates.NORMAL, type?: number) {
        super(ID, symName, state, type);
        this.InitExtra();
    }

    private _skullPopped: boolean = false;

    get skullPopped(): boolean {
        return this._skullPopped;
    }

    set skullPopped(value: boolean) {
        this._skullPopped = value;
    }

    private _isWildFireOn: boolean = false;

    get isWildFireOn(): boolean {
        return this._isWildFireOn;
    }

    set isWildFireOn(value: boolean) {
        this._isWildFireOn = value;
    }

    public get symbolHeight(): number {
        return PunkSymbolData.fetchSymSize(this.sizeType.value);
    }

    public createDivider(value: boolean, forMysteryRevealSym: boolean = false): void {
        if (!this.isLowValue && !forMysteryRevealSym) {
            return;
        }

        if (value && !this.divider.length) {
            let div = this.addChild(new Sequence({texturePath: "RowDivider"}));
            div.anchor.set(0.5, 0.5);
            div.blendMode = PIXI.BLEND_MODES.ADD;
            div.position.y = PunkSymbolData.fetchSymSize(this.sizeType.value) / 2;
            div.visible = false;
            div.name = "chalkDivider";
            this.divider.push(div);
        }
    }

    public enableDivider(value: boolean, forMysteryRevealSym: boolean = false): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        if (!this.isLowValue && !forMysteryRevealSym) return tl;

        if (value && this.divider.length) {
            this.divider.forEach((d: Sequence) => {
                tl.add(d.play({
                    duration: 0.01,
                    onStart: () => {
                        d.visible = true;
                    }
                }), 0);
            });
        }

        return tl;
    }

    public onSpinStart(): void {
        /*if (this.fire_FX && !this.fireDied) {
            this.fire_FX.stop();

            this.fire_FX = this.addChild(new Sequence({texturePath: "WildIdle"}));
            this.fire_FX.blendMode = BLEND_MODES.ADD;
            this.fire_FX.zIndex = 300;
            this.fire_FX.anchor.set(0.5, 0.5);
            this.fire_FX.play({repeat: -1});
        }*/

        // #361
        if (this.fire_FX) {
            this.fire_FX.stop();
        }

        if (this.sizeType.value > 4 && this.isLowValue) {
            this.mainSp.visible = false;
            this.subSp.visible = false;
        }

        if (this.winSym.length) {
            this.winSym.forEach(w => w.Stop(0.25));
        }
    }

    public markNonWin(enable: boolean, alpha: number = 0.55, durr: number = 0.25): void {
        //if (this.isScatter || this.isMystery || this.isXWild) {
        if (this.isScatter || this.isMystery) {
            super.markNonWin(false, alpha, durr)
        } else {
            super.markNonWin(enable, alpha, durr);
        }

        // #263
        if (this._darkSquareGfx && this.isLowValue) {
            this.addChild(this._darkSquareGfx);
        }
    }

    public setInteractive(val: boolean): void {
        if (this.isWild || this.isScatter) return;

        if (this.mainSp) {
            this.mainSp!.interactive = val;
            this.mainSp!.on('pointerdown', () => {
                MiniPayTable.instance.onClicked({
                    dispObj: this.mainSp,
                    name: this.symName
                })
            });
        }

    }

    playBigWin(hits: boolean, durr: number, isBonusGame: boolean = false): void {
        if (hits) {
            this.useWinSymbol();
            this.hideExpandSpine(isBonusGame);
            this.winSym.forEach(w => w.BigWinStart(durr));
        }
        this.markNonWin(!hits, 0.6, durr);
    }

    flashBlack() {
        gsap.fromTo(this._darkSquareGfx, {alpha: 1}, {alpha: 0, duration: 0.75});
    }

    playWin(hits: boolean, duration: number, winLvl: number = 0, isBonusGame: boolean = false) {
        if (this.isScatter) return;
        if (hits) {
            if (this.useWinSymbol(hits)) {
                this.hideExpandSpine(isBonusGame);
                this.winSym.forEach(w => w.Play(winLvl, duration));
            }
        }
        // this.markNonWin(!hits, undefined, duration);
    }

    stopWin(duration: number, ignoreDarknessChange?: boolean) {
        if (this.isScatter) return;

        if (this.useWinSymbol(false)) {
            this.winSym.forEach(w => w.Stop(duration));
        }

        if (!ignoreDarknessChange)
            this.markNonWin(false, undefined, duration);
    }

    // #364
    public shakeMultiBeforeMysteryTransform(toSymName: string, toSymSize: number): gsap.core.Timeline {
        this._expandedSymSize = toSymSize;
        this.createExpandSpine(toSymName, toSymSize);
        this._expandSpine.visible = false;

        const tl: gsap.core.Timeline = gsap.timeline(),
            finalMultText: string = "x" + toSymSize,
            countUpDurr: number = this._expandSpine.fetchDuration(finalMultText);

        if (toSymSize < 6) {
            return tl;
        }

        tl.fromTo(this.multText, {
            text: "x4",
        }, {
            duration: countUpDurr,
            text: finalMultText,
            ease: "none",
            onUpdate: () => {
                const getCurrCount: number = parseInt(this.multText.text.replace("x", ""));
                this.multText.text = "x" + getCurrCount;
            },
            onComplete: () => {
                this.multText.text = finalMultText;
            }
        });

        return tl;
    }

    // #364
    public doLighteningBeforeMysteryTransform(toSymName: string, toSymSize: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        if (toSymSize < 9) {
            return tl;
        }

        this.createExpandLighteningSpine(toSymSize);

        let animName: string = "animation";
        if (toSymName === "M1") {
            animName += toSymName;
        }
        tl.add([
            this._expandLighteningSpine.play({
                animation: animName,
                showOnEnd: true,
            }),
            () => {
                this.mainSp.alpha = 0;

                if (this.isLowValue) {
                    this.addColorGraphic();
                    this.sortChildren();
                }
            }
        ]);

        return tl;
    }

    public transformMystery(toSymName: string, toSymSize: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            currSymPosX: number = this.position.x,
            currSymPosY: number = this.position.y,
            finalMultText: string = "x" + toSymSize;

        //tl.add(this.updateMultText(toSymSize, currSymPosX, currSymPosY), 0);

        tl.add(() => {
            this._expandSpine.visible = true;
            if (this._expandLighteningSpine) {
                this._expandLighteningSpine.stop();
            }

            this.changeState({
                forceState: true,
                state: ReelSymbolStates.NORMAL,
                duration: 0,
                sym: toSymName
            });

            this.mainSp.alpha = 0;

            if (this.isLowValue) {
                this.addColorGraphic();
                this.sortChildren();
            }
        }, Math.max(0.001, tl.duration()));

        tl.add([
            this._expandSpine.play({
                animation: finalMultText,
                showOnEnd: true,
            }),
            () => {
                if (toSymSize < 6) {
                    this.multText.visible = false;
                }
            }
        ], tl.duration());

        return tl;
    }

    changeXWildValue(toType: string, symSize: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = Helper.setTimeline();

        tl.add(this.changeState({
            state: ReelSymbolStates.NORMAL,
            sizeType: symSize,
            duration: 0.001,
            sym: toType,
            forceState: true
        }));

        tl.to(this.multText, {
            alpha: 0,
            duration: 0.3
        });

        tl.to(this, {
            duration: 1
        });

        return tl;
    }

    jumpWildTransform(): void {
        this.changeState({
            state: ReelSymbolStates.NORMAL,
            sizeType: this.sizeType.value,
            duration: 0.4,
            sym: "W",
            forceState: true
        })
        this.divider.forEach(d => d.destroy());
        this.divider = [];
    }

    public WildTransform(): gsap.core.Timeline {
        const sequenceName: string = (this.sizeType.value === 1 ? "wTransform1" : "wTransform"),
            wTransform: OverlayItem = PunkRocker.overlayAnim.playSequence(this, PunkRocker.assetLoader.getTextures(sequenceName), {
                onIndex: 0
            }),
            tl: gsap.core.Timeline = wTransform.timeline.add(() => {
                const durr: number = (GameSetting.isFastSpin || ReelEngineManager.isQuickStop) ? 0.01 : 0.3;

                this.triggerWild(true);
                this.changeState({
                    state: ReelSymbolStates.NORMAL,
                    sizeType: this.sizeType.value,
                    duration: durr,
                    sym: "W",
                    forceState: true
                });
            }, 0.1);

        this.divider.forEach(d => d.destroy());

        return tl;
    }

    public triggerWild(forced?: boolean): void {
        if (!this.isWild && !forced) return;

        const wildFire: Sequence = (PunkRocker.overlayAnim.playSequence(this, PunkRocker.assetLoader.getTextures("WildFire")).item as Sequence),
            wildIgnite: Sequence = (PunkRocker.overlayAnim.playSequence(this, PunkRocker.assetLoader.getTextures("WildIgnite")).item as Sequence),
            yScale: number = (this.symbolHeight / 180);

        wildFire.blendMode = BLEND_MODES.ADD;
        wildFire.scale.y = yScale;

        wildIgnite.blendMode = BLEND_MODES.ADD;
        wildIgnite.scale.y = yScale;

        this.fire_FX = (PunkRocker.overlayAnim.playSequence(this, PunkRocker.assetLoader.getTextures("WildIdle"), {repeat: -1}).item as Sequence);
        this.fire_FX.blendMode = BLEND_MODES.ADD;
        this.fire_FX.zIndex = 300;
        this.fire_FX.anchor.set(0.5, 0.5);
        this.fire_FX.scale.y = yScale;

        this.isWildFireOn = true;
    }

    public killFire(): void {
        if (!this.fire_FX) return;

        this.fire_FX.stop();
        this.fire_FX = this.addChild(new Sequence({texturePath: "WildDied"}));
        this.fire_FX.blendMode = BLEND_MODES.ADD;
        this.fire_FX.zIndex = 300;
        this.fire_FX.anchor.set(0.5, 0.5);
        this.fire_FX.play({duration: 1}).to(this.fire_FX, {alpha: 0, duration: this.fire_FX.timeline?.duration()}, 0);
        this.isWildFireOn = false;
    }

    showMult(durr: number, toSize: number): void {
        const multToShow: number = (toSize > 7) ? 4 : toSize,
            isLargeMult: boolean = toSize > 7;
        this.multText = this.addChildAt(new SymbolMultiplyCounter(multToShow - 3), this.children.length);
        const to: number = this.isXWild ? 0 : (this.multText.position.y + PunkSymbolData.fetchSymSize(toSize) / 4) * (isLargeMult ? -1 : 1);
        gsap.fromTo(this.multText, {alpha: 0}, {alpha: 1, duration: durr * 1.5, y: to, ease: "back.out(1.7)"});
        gsap.fromTo(this.multText.scale, {x: 0.5, y: 0.5}, {x: 1, y: 1, duration: durr * 1.5});
        this.sortChildren();
    }

    public showSymMultiplier(durr: number, multTo: number): void {
        this.multText = this.addChildAt(new SymbolMultiplyCounter(multTo), this.children.length);

        const isLargeMult: boolean = multTo > 4,
            posTo: number = this.isXWild ? 0 : (this.multText.position.y + PunkSymbolData.fetchSymSize((multTo + 3)) / 4) * (isLargeMult ? -1 : 1); // (multTo + 3) --> 2 other + 1 buffer symbols in reel

        gsap.fromTo(this.multText, {alpha: 0}, {alpha: 1, duration: durr * 1.5, y: posTo, ease: "back.out(1.7)"});

        gsap.fromTo(this.multText.scale, {x: 0.5, y: 0.5}, {x: 1, y: 1, duration: durr * 1.5});
        this.sortChildren();
    }

    playLowRevealDividers(applyOnBottom: boolean): void {

        let desiredSize = PunkSymbolData.fetchSymSize(8 - this.sizeType.value);
        let symbolSize = PunkSymbolData.fetchSymSize(1);
        let totalSize = desiredSize * this.nrOnMult;
        let startSize = symbolSize + (desiredSize - symbolSize);

        for (let i = 0; i < this.nrOnMult - (applyOnBottom ? 0 : 1); i++) {
            let div = this.addChild(new Sequence({texturePath: "RowDivider"}));
            div.anchor.set(.5, .5);
            div.blendMode = PIXI.BLEND_MODES.ADD;
            div.position.y = -totalSize / 2 + desiredSize + (i * startSize);
            div.visible = false;
            div.name = "chalkDivider";
            this.divider.push(div);
        }

        this.enableDivider(true);
    }

    skipped(): void {
        this._timeLine?.totalProgress(1);
        this._subTimeline?.totalProgress(1);
    }

    public setVisible(value: boolean): void {
        super.setVisible(value);
        if (this.winSym.length) {
            this.winSym.forEach(w => w.visible = value);
        }
    }

    playFlash(durr: number = 0.25) {
        // this._subTimeline = this.fetchSubTimeline(undefined, true);
        gsap.fromTo(this.flashGFX, {alpha: 1}, {duration: durr, alpha: 0});
    }

    public stopSpark(durr: number = 0.2): void {
        if (!this.flyByAnimation) {
            return;
        }
        gsap.to(this.flyByAnimation, {
            alpha: 0,
            duration: durr,
            ease: "power3.in"
        }).eventCallback("onComplete", () => this.flyByAnimation.stop(true));
    }

    playScatterAnim(scatterNumber: number, isRespinRound: boolean = false, transformToRiot: boolean = false): gsap.core.Timeline {
        if (!(this.isScatter)) {
            return Helper.setTimeline();
        }

        const tl: gsap.core.Timeline = new gsap.core.Timeline(),
            landingAnimName: string = scatterNumber + (isRespinRound || transformToRiot ? "_riot" : "") + (transformToRiot ? "_transform" : "_landing"),
            animDurr: number = this._scatterSpine.fetchDuration(landingAnimName);

        tl.add(() => {
            this._scatterSpine.play({
                animation: landingAnimName,
                showOnEnd: true
            });
        }, 0);

        tl.to(this, {
            duration: animDurr
        }, 0);

        return tl;
    }

    playScatterLoop(scatterNumber: number, isRespinRound: boolean = false): gsap.core.Timeline {
        if (!(this.isScatter)) {
            return Helper.setTimeline();
        }

        if (this.skullPopped) {
            return Helper.setTimeline();
        }

        const tl: gsap.core.Timeline = new gsap.core.Timeline(),
            loopedAnimName: string = scatterNumber + (isRespinRound ? "_riot" : "") + "_loop";

        tl.add(() => {
            this._scatterSpine.play({
                animation: loopedAnimName,
                showOnEnd: true,
                loop: true
            });
        });

        tl.to(this, {
            duration: 0.001
        });

        return tl;
    }

    turnScatterToRiot(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();

        tl.add(this.playScatterAnim(2, false, true));
        tl.add(this.turnStaticTexturesToRiot());

        return tl;
    }

    turnScatterWithSkull(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();

        tl.add(this._scatterSpine.play({
            animation: "1_2_transform",
            showOnEnd: true
        }));

        return tl;
    }

    showPoppedSkullOnScatters(): gsap.core.Timeline {
        this.skullPopped = true;

        if (this.mainSp) {
            this._mainSp.visible = false;
        }

        if (this.subSp) {
            this.subSp.visible = false;
        }

        if (this._bgGFX) {
            this._bgGFX.visible = false;
        }

        const tl: gsap.core.Timeline = new gsap.core.Timeline();

        tl.to(this, {
            duration: this._scatterSpine.fetchDuration("allSkullPop"),
            onStart: () => {
                this._scatterSpine.play({
                    animation: "allSkullPop",
                    showOnEnd: true
                });
            }
        });

        return tl;
    }

    onSkullPopped(): void {
        this.alpha = 0;
    }

    burnTrainFromScatter(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();

        tl.to(this, {
            duration: this._scatterSpine.fetchDuration("5_trainFireTrigger"),
            onStart: () => {
                this._scatterSpine.play({
                    animation: "5_trainFireTrigger",
                    showOnEnd: true
                });
            }
        });

        return tl;
    }

    hideRiotSpine(): void {
        if (this._scatterSpine) {
            this._scatterSpine.stop();
        }
    }

    playScatterHit(): void {
        (PunkRocker.overlayAnim.playSequence(this, PunkRocker.assetLoader.getTextures("WildFire")).item as Sequence).blendMode = BLEND_MODES.ADD;
    }

    remove(): void {
        if (this.winSym.length) {
            this.winSym.forEach(w => w.remove());
            this.winSym = [];
        }
        super.remove();
    }

    playPipeBombAnimation(animName: string, symSize: number, isLooped: boolean): gsap.core.Timeline {
        if (!symSize) {
            return Helper.setTimeline();
        }

        if (!this._pipeBombSpine) {
            this.createPipeBombAnim();
        }

        const tl: gsap.core.Timeline = new gsap.core.Timeline(),
            animDurr: number = this._pipeBombSpine.fetchDuration(animName),
            timelineDurr: number = isLooped ? 0.01 : animDurr;

        tl.add(() => {
            // #342 safe check
            if (this._pipeBombSpine.parent) {
                this._pipeBombSpine.play({
                    animation: animName,
                    showOnEnd: true,
                    skinType: "xWild_" + symSize,
                    loop: isLooped
                });
            }
        }, 0);

        if (animName === "landing" || animName === "growExplosion") {
            tl.add(() => {
                if (this.mainSp) {
                    this._mainSp.visible = false;
                }

                if (this._bgGFX) {
                    this._bgGFX.visible = false;
                }

                if (this.divider && this.divider.length > 0) {
                    this.divider.forEach(item => {
                        item.visible = false;
                    });
                }
            }, 0.3);
        }

        if (animName === "explode") {
            tl.add(() => {
                this.mainSp.texture = this.subSp.texture = PunkRocker.assetLoader.getTextures("XWILD/" + symSize + "/XWILD_normal")[0];
                this.divider.forEach((chalkDivider) => {
                    chalkDivider.visible = false;
                });
            });
        }

        if (animName === "landing") {
            // #407
            if(!(ReelEngineManager.isQuickStop)) {
                tl.add(() => {
                    // #396
                    //if (!GameConfig.xWILD_PRESENTATION_STARTED) {
                    SoundManager.playWildFuseSound();

                    // #342 safe check
                    if (this._pipeBombSpine.parent) {
                        this._pipeBombSpine.play({
                            animation: "landingLoop",
                            showOnEnd: true,
                            skinType: "xWild_" + symSize,
                            loop: true
                        });
                    }
                    //}
                }, timelineDurr);
            }
        }

        return tl;
    }

    protected InitExtra(): void {
        this.isWildFireOn = false;
        this.skullPopped = false;
        this.createFlashGraphic();
        if (this.symName === "S1") {
            this.createScatterSpine();
            this._overlay = new PIXI.Sprite(ImgLoader.getImgTexture("S_overlay"));
            this._overlay.anchor.set(0.5,0.5);
            this.addChild(this._overlay);
        }
        if (this.isXWild) {
            this.createPipeBombAnim();
        }
        this.addColorGraphic();

        if (this.state.equal(ReelSymbolStates.SPIN) && this.isWild) {
            this.flyByAnimation = this.addChild(new Sequence({texturePath: "WildFireSpin"}));
            this.flyByAnimation.zIndex = 500;
            this.sortChildren();
            this.flyByAnimation.blendMode = BLEND_MODES.ADD;
            this.flyByAnimation.anchor.set(0.5, 0.5);
            this.flyByAnimation.position.y = this.isScatter ? 0 : -80;
            this.flyByAnimation.play({
                repeat: -1, onCompleteCallback: () => {
                    this.flyByAnimation.visible = false
                }
            });
        }


    }

    protected createFlashGraphic(): void {
        this.flashGFX = this.addChild(Sequence.createSequence(PunkRocker.assetLoader.getTextures("Sector_Attention")));// this.addChild(new PIXI.Graphics());
        this.flashGFX.blendMode = BLEND_MODES.ADD;
        this.flashGFX.alpha = 0;
        this.flashGFX.zIndex = 290;
        this.flashGFX.scale.y = PunkSymbolData.fetchSizeTimesSize(this.sizeType.value);
        this.sortChildren();
    }

    protected addColorGraphic(): void {
        if (this.isLowValue) {

            this._bgGFX = this.addChild(new PIXI.Graphics());
            this._bgGFX.name = "bgGFX";

            let color: number = 0x000000;
            switch (parseInt(this.symName[1], 16)) {
                case 1:
                    color = 0xec003a;
                    break;
                case 2:
                    color = 0xff9b00;
                    break;
                case 3:
                    color = 0x00d701;
                    break;
                case 4:
                    color = 0x30687b;
                    break;
                case 5:
                    color = 0xa8892b;
                    break;
            }

            let h = PunkSymbolData.fetchSymSize(this.sizeType.value);
            this._bgGFX.alpha = 0.2;
            this._bgGFX.beginFill(color, 1);
            this._bgGFX.drawRect(-this.symbolWidht * 0.5, -h * 0.5, this.symbolWidht, h);
            this._bgGFX.height = h;
            this._bgGFX.endFill();

            this._bgGFX.zIndex = -1;
            this._bgGFX.blendMode = BLEND_MODES.OVERLAY;

            this.sortChildren();
        }
    }

    protected setSymbolState(config: ISymbolAnimationConf): gsap.core.Timeline {
        this._timeLine = Helper.setTimeline(this._timeLine, new gsap.core.Timeline({
            onComplete: () => {
                if (this.subSp && this.mainSp && this.parent) {
                    this.subSp.scale = this.mainSp.scale;
                    this.subSp.texture = this.mainSp.texture;
                }
            }
        }), true);

        const toConf: ISymbolConfig = config.sym ? SymbolData.getConfig(config.sym!) : this.symbolConfig;
        const toSizeType: any = config.sizeType && this.sizeType.value != config.sizeType ? config.sizeType! : this.sizeType.value;
        const toState: any = config.state && this.state.value != config.state ? config.state! : this.state.value;

        let TEXTURE_FROM: PIXI.Texture[] | undefined,
            TEXTURES_TO: PIXI.Texture[] | undefined;

        if (this.isMystery) {
            if (config.sym) {
                const maxTextureFromSize: number = (this.sizeType.value >= 7) ? 7 : this.sizeType.value;
                TEXTURE_FROM = SymbolData.getTextures(this.symbolConfig.NAME, this.state.value, maxTextureFromSize.toString());
                TEXTURES_TO = SymbolData.getTextures(toConf.NAME, toState, toSizeType);
            } else {
                const maxTextureFromSize: number = (this.sizeType.value >= 7) ? 7 : this.sizeType.value,
                    maxTextureToSize: number = (toSizeType >= 7) ? 7 : toSizeType;

                TEXTURE_FROM = SymbolData.getTextures(this.symbolConfig.NAME, this.state.value, maxTextureFromSize.toString());
                TEXTURES_TO = SymbolData.getTextures(toConf.NAME, toState, maxTextureToSize.toString());
            }
        } else {
            TEXTURE_FROM = SymbolData.getTextures(this.symbolConfig.NAME, this.state.value, this.sizeType.value);
            TEXTURES_TO = SymbolData.getTextures(toConf.NAME, toState, toSizeType);
        }

        this.state.set(toState);

        this.expandAnimation(toSizeType, config.duration ? config.duration : 0, config.ease);

        this.sizeType.set(toSizeType);

        this.symbolConfig = toConf;

        this.textureAnimation(TEXTURE_FROM, TEXTURES_TO, config.duration ? config.duration : 0, config.ease ? config.ease : Power4.easeInOut);

        return this._timeLine;
    }

    protected expandAnimation(size: number, _duration: number, _ease: any): void {
        if (!this.sizeType.equal(size)) {

            const currSize: number = PunkSymbolData.fetchSizeTimesSize(this.sizeType.value);
            const expectedSize: number = PunkSymbolData.fetchSizeTimesSize(size);

            const toScale: PIXI.Point = new PIXI.Point(1, expectedSize / currSize);
            const fromScale: PIXI.Point = new PIXI.Point(1, currSize / expectedSize);

            // Texture
            this._timeLine!.fromTo(this.mainSp.scale, {x: fromScale.x, y: fromScale.y}, {
                x: 1,
                y: 1,
                duration: _duration,
                ease: _ease
            }, 0)
            this._timeLine!.fromTo(this.subSp.scale, {x: 1, y: 1}, {
                x: toScale.x,
                y: toScale.y,
                duration: _duration,
                ease: _ease
            }, 0);

            // GFX
            this._timeLine!.fromTo([this._darkSquareGfx.scale, this.flashGFX.scale], {x: 1, y: 1}, {
                x: toScale.x,
                y: toScale.y,
                duration: _duration,
                ease: _ease
            }, 0);
            // this._timeLine!.fromTo(this.flashGFX.scale, { x: 1, y: 1 }, { x: toScale.x, y: toScale.y, duration: _duration, ease: _ease }, 0);
            if (this._bgGFX) {
                this._timeLine!.fromTo(this._bgGFX.scale, {x: 1, y: 1}, {
                    x: toScale.x,
                    y: toScale.y,
                    duration: _duration,
                    ease: _ease
                }, 0);
            }
            if (this.divider.length) {
                const v = (this.symbolHeight / 2) * expectedSize;
                this._timeLine!.to(this.divider, {y: v, duration: _duration, ease: _ease}, 0);
            }

            // this._timeLine!.add(() => this.updateGraphics());
            this.sizeType.set(size);
        }
    }

    protected textureAnimation(_TextureFrom: PIXI.Texture[] | undefined, _TextureTo: PIXI.Texture[] | undefined, _duration: number, _ease?: any): void {
        if (_TextureTo) {

            this.mainSp!.texture = _TextureTo[0];
            this._timeLine!.fromTo(this.mainSp, {alpha: 0}, {duration: _duration, alpha: 1, ease: _ease}, 0)

            if (_TextureFrom) {
                this.subSp!.texture = _TextureFrom[0];
                this._timeLine!.fromTo(this.subSp, {alpha: 1}, {duration: _duration, alpha: 0, ease: _ease}, 0);
            }
        }
    }

    private turnStaticTexturesToRiot(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();

        tl.to(this, {
            duration: 0.001
        });

        tl.add(() => {
            this.mainSp.texture = this.subSp.texture = PunkRocker.assetLoader.getTextures("S/4/S_riot")[0];
        });

        return tl;
    }

    private createPipeBombAnim(): void {
        const spineName: string = "pipeBomb";
        this._pipeBombSpine = new SpinePlayer(spineName);
        this._pipeBombSpine.name = spineName;
        this.addChildAt(this._pipeBombSpine, 3);
    }

    private createScatterSpine(): void {
        const spineName: string = "scatterSymbol";
        this._scatterSpine = new SpinePlayer(spineName);
        this._scatterSpine.name = spineName;
        this.addChildAt(this._scatterSpine, 3);
    }

    // #364
    private createExpandLighteningSpine(toSymSize: number): void {
        const spineName: string = "expandingLightning";
        this._expandLighteningSpine = new SpinePlayer(spineName);
        this._expandLighteningSpine.name = spineName;

        if (toSymSize === 2) {
            this._expandLighteningSpine.position.set(0, 135);
        } else if (toSymSize === 3) {
            this._expandLighteningSpine.position.set(0, 162);
        } else {
            this._expandLighteningSpine.position.set(0, 180);
        }

        this.addChildAt(this._expandLighteningSpine, 3);
    }

    private createExpandSpine(toSymName: string, toSymSize: number): void {
        const spineName: string = "expand_" + toSymName;
        this._expandSpine = new SpinePlayer(spineName);
        this._expandSpine.name = spineName;

        if (toSymSize === 2) {
            this._expandSpine.position.set(0, 135);
        } else if (toSymSize === 3) {
            this._expandSpine.position.set(0, 162);
        } else {
            this._expandSpine.position.set(0, 180);
        }

        this.addChildAt(this._expandSpine, 2);
    }

    private hideExpandSpine(isBonusGame: boolean): void {
        if (this._expandSpine) {
            let flag: boolean = false;

            if (isBonusGame) {
                flag = true;
            } else {
                if (this._expandedSymSize > 4) {
                    flag = true;
                }
            }

            if (flag) {
                this._expandSpine.visible = false;
                this.multText.visible = true;
                this.mainSp.alpha = 1;
            }
        }
    }

    private useWinSymbol(shouldBeCreated: boolean = true): boolean {
        if (!this.winSym.length && this.mainSp && shouldBeCreated) {
            for (let k = 0; k < this.splitSym.length + 1; k++) {
                const sizeToShow: number = (this.sizeType.value >= 13) ? 7 : this.sizeType.value,
                    textureToShow: PIXI.Texture[] | undefined = SymbolData.getTextures(this.symName, "normal", sizeToShow.toString())!;

                this.winSym.push(new PunkWinSymbol(this.posID[0], this.symName, "normal", this.sizeType.value, (this.sizeType.value >= 13) ? textureToShow[0] : this.mainSp.texture));

                this.mainSp!.texture = this.subSp!.texture = (this.sizeType.value >= 13) ? textureToShow[0] : this.mainSp.texture;
            }
        }

        if (this.winSym.length) {
            this.winSym.forEach((w, i) => {
                w.position = Helper.copyLocalPosition(w, i ? this.splitSym[i - 1] : this.mainSp)
                w.zIndex = (w.position.x / 720) * 10 + (w.position.y / 720) * 5;
                w.parent.sortChildren();
            });
        }

        return !!this.winSym;
    }

    private updateMultText(toSymSize: number, posX: number, posY: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            currMultText: number = parseInt(this.multText.text.replace("X", ""));

        if (toSymSize > currMultText) {
            const sizeStepTo_10: number = Math.min(10, toSymSize),
                countUpDurr_10: number = 0.5,
                countUpDelay_10: number = 0;

            tl.fromTo(this.multText, {
                text: "x4",
            }, {
                duration: countUpDurr_10,
                delay: countUpDelay_10,
                text: "x" + sizeStepTo_10,
                onStart: () => {
                    this.shakeSymbol(1.5, 1.5, countUpDurr_10, 2.75, posX, posY);
                },
                onUpdate: () => {
                    const getCurrCount: number = parseInt(this.multText.text.replace("x", ""));
                    this.multText.text = "x" + getCurrCount;
                },
                onComplete: () => {
                    this.multText.text = "x" + sizeStepTo_10;
                }
            });

            if (toSymSize > sizeStepTo_10) {
                const sizeStepTo_15: number = Math.min(15, toSymSize),
                    countUpDurr_15: number = 0.5,
                    countUpDelay_15: number = 0.3;

                tl.fromTo(this.multText, {
                    text: "x10",
                }, {
                    duration: countUpDurr_15,
                    delay: countUpDelay_15,
                    text: "x" + sizeStepTo_15,
                    onStart: () => {
                        this.shakeSymbol(1.5, 1.5, countUpDurr_15, 2.75, posX, posY);
                    },
                    onUpdate: () => {
                        const getCurrCount: number = parseInt(this.multText.text.replace("x", ""));
                        this.multText.text = "x" + getCurrCount;
                    },
                    onComplete: () => {
                        this.multText.text = "x" + sizeStepTo_15;
                    }
                });

                if (toSymSize > sizeStepTo_15) {
                    const countUpDurr_20: number = 0.5,
                        countUpDelay_20: number = 0.3;

                    tl.fromTo(this.multText, {
                        text: "x15",
                    }, {
                        duration: countUpDurr_20,
                        delay: countUpDelay_20,
                        text: "x" + toSymSize,
                        onStart: () => {
                            this.shakeSymbol(1.5, 1.5, countUpDurr_20, 2.75, posX, posY);
                        },
                        onUpdate: () => {
                            const getCurrCount: number = parseInt(this.multText.text.replace("x", ""));
                            this.multText.text = "x" + getCurrCount;
                        },
                        onComplete: () => {
                            this.multText.text = "x" + toSymSize;
                        }
                    });
                }
            }
        }

        return tl;
    }

    private shakeSymbol(shakeX: number, shakeY: number, durr: number, shakeIntensity: number, posX: number, posY: number): gsap.core.Timeline {
        return AnimationEffects.Shake({
            x: shakeX,
            y: shakeY,
            obj: this,
            hertz: 80,
            duration: durr,
            updateOnComplete: true,
            positionCb: () => new PIXI.Point(posX, posY),
            Intensity: shakeIntensity,
            ease: Power2.easeOut
        }).play();
    }

    /*private caseOfLowReveal(): void {

        let reverseSmallSize: number = 8 - (this.sizeType.value > 7 ? 7 : this.sizeType.value);
        this.winSym.push(new PunkWinSymbol(this.posID[0], this.symName, "normal", reverseSmallSize, this.mainSp.texture));

        for (let i = 0; i < this.nrOnMult - 1; i++) {
            this.winSym.push(new PunkWinSymbol(this.posID[0], this.symName, "normal", reverseSmallSize, this.mainSp.texture));
            let sp = this.addChild(new PIXI.Sprite(this.mainSp.texture));
            sp.anchor.set(0.5, 0.5);
            this.splitSym.push(sp);
        }

        let desiredSize = PunkSymbolData.fetchSymSize(reverseSmallSize);
        let symbolSize = PunkSymbolData.fetchSymSize(1);
        let totalSize = desiredSize * this.nrOnMult;
        let startSize = symbolSize + (desiredSize - symbolSize);

        [this.mainSp, this.subSp].concat(this.splitSym).forEach((s, i) => {
            if (i == 0 || i == 1) {
                s.position.y = -totalSize / 2 + (startSize / 2);
            } else
                s.position.y = -totalSize / 2 + (-startSize / 2) + (i * startSize);
        });

        this.addColorGraphic();
        this.sortChildren();
    }*/
}
